/**
 * User roles inside a workshop: three levels (owner's decision, 2026-11-04): Tulajdonos (owner),
 * Adminisztrátor (manager: the former Vezető and Iroda) and Szerelő (mechanic). The database enum
 * `workshop_role` still has 'office' for old app versions; the database turns it into 'manager'.
 * Texts: `role.<role>.label` and `role.<role>.description`.
 */
export const ROLES = ["owner", "manager", "mechanic"] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

/** A role read from the database: the old 'office' is an administrator now. */
export function roleOf(value: string | null | undefined): Role | null {
  if (value === "office") return "manager";
  return value && isRole(value) ? value : null;
}

/**
 * What a member may do, the same list as public.can_do() in the database (which enforces it):
 * the administrator all but `settings`, `users` and `subscription`; a mechanic only `items` and `prices`,
 * and only when the owner switched them on (MECHANIC_SWITCHES).
 */
export const ACTIONS = [
  "items", "prices", "close", "invoice", "quote", "notice", "stock", "reminders", "aiAsk", "aiBuy",
  "customerDelete", "import", "reports", "settings", "users", "subscription",
] as const;
export type Action = (typeof ACTIONS)[number];
const OWNER_ONLY: readonly Action[] = ["settings", "users", "subscription"];

/** The owner's two switches for mechanics (Settings, Jogosultságok), both off by default. */
export const MECHANIC_SWITCHES = { items: "workOrder.items", prices: "workOrder.prices" } as const;

/** Defaults of the permission lists, the same as public.default_permission_roles() in the database. */
export const DEFAULT_PERMISSIONS: Record<string, Role[]> = {
  "workOrder.reopen": ["owner"],
  "workOrder.delete": ["owner", "manager"],
  "workOrder.items": ["owner", "manager"],
  "workOrder.prices": ["owner", "manager"],
};
/** The permission lists the owner sets for the administrator (the mechanic switches are separate). */
export const ADMIN_PERMISSIONS = ["workOrder.reopen", "workOrder.delete"] as const;

export type Permissions = Record<string, readonly string[] | undefined>;

/** public.has_permission(): the owner always; a mechanic only the two switches; 'office' counts as 'manager'. */
export function hasPermission(role: Role | null, permissions: Permissions, permission: string): boolean {
  if (!role) return false;
  if (role === "owner") return true;
  if (role === "mechanic" && permission !== MECHANIC_SWITCHES.items && permission !== MECHANIC_SWITCHES.prices) return false;
  const list = (permissions[permission] ?? DEFAULT_PERMISSIONS[permission] ?? ["owner"]).map((r) => roleOf(r));
  return list.includes(role);
}

/** public.can_do(). */
export function roleCan(role: Role | null, permissions: Permissions, action: Action): boolean {
  if (!role) return false;
  if (role === "owner") return true;
  if (role === "manager") return !OWNER_ONLY.includes(action);
  if (action === "items") return hasPermission(role, permissions, MECHANIC_SWITCHES.items);
  if (action === "prices") return hasPermission(role, permissions, MECHANIC_SWITCHES.prices);
  return false;
}

/** The permission lists with a mechanic switch set (the administrator and the owner always stay). */
export function withMechanicSwitch(permissions: Permissions, key: keyof typeof MECHANIC_SWITCHES, on: boolean): Record<string, Role[]> {
  const out: Record<string, Role[]> = {};
  for (const [k, v] of Object.entries(permissions)) if (v) out[k] = [...new Set(v.map((r) => roleOf(r)).filter((r): r is Role => !!r))];
  out[MECHANIC_SWITCHES[key]] = on ? ["owner", "manager", "mechanic"] : ["owner", "manager"];
  return out;
}

export const MIN_PASSWORD_LENGTH = 8;

/** Roles that do workshop work and can be a work order's mechanic (every level since 2026-11-04). */
export const MECHANIC_ROLES: readonly Role[] = ["mechanic", "owner", "manager"];

export interface NamedMember {
  userId: string;
  email: string;
  role: Role;
  displayName?: string | null;
}

/** The name shown for a member: the given name, else the part of the e-mail address before the @. */
export function memberName(m: Pick<NamedMember, "email" | "displayName">): string {
  const name = m.displayName?.trim();
  return name || m.email.split("@")[0] || m.email;
}

/**
 * Who can be chosen as a mechanic, mechanics first (owner, 2026-11-04), then owners and managers, each by name.
 */
export function mechanicChoices<T extends NamedMember>(members: readonly T[]): T[] {
  const rank = (r: Role) => MECHANIC_ROLES.indexOf(r);
  return members
    .filter((m) => rank(m.role) >= 0)
    .sort((a, b) => rank(a.role) - rank(b.role) || memberName(a).localeCompare(memberName(b)));
}

/** The mechanic of a line as it counts: its own, else the work order's responsible mechanic. */
export function lineMechanic(line: { kind: string; mechanicId?: string | null }, orderMechanicId: string | null | undefined): string | null {
  if (line.kind !== "labour") return null;
  return line.mechanicId ?? orderMechanicId ?? null;
}

/** "Saját munkalapjaim": the user is the responsible mechanic or has a labour line of their own. */
export function isMyWorkOrder(o: { mechanicId?: string | null; lineMechanicIds?: readonly (string | null)[] }, userId: string): boolean {
  return o.mechanicId === userId || (o.lineMechanicIds ?? []).includes(userId);
}
